import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Check, CircleOff, Eraser } from 'lucide-react';
import { useTools } from "../../../ToolProvider.jsx";
import { PALETTE, VACIADO_COLOR } from "../../../colorPalette.js";
import { normalizeCustomColor } from "../../../customColors.js";
import CustomColorControls from "../../CustomColorControls/CustomColorControls.jsx";

// Un color que no es de la paleta (C3D-17) se enseña como propio, no como el
// primero de la paleta.
const entryFor = (value) => {
    const fromPalette = PALETTE.find(entry => entry.value.toLowerCase() === String(value || '').toLowerCase());
    if (fromPalette) return fromPalette;
    const custom = normalizeCustomColor(value);
    return custom ? { value: custom, description: 'Personalizado' } : null;
};

const ColorPicker = ({typee, className, value, onChange, allowVaciado = typee === 'object'}) => {
    // useTools() puede devolver undefined fuera del árbol de ToolProvider (p.
    // ej. reutilizando este selector en el visor público); solo hace falta
    // cuando no se pasa un `onChange` propio.
    const {
        onChangeColor: changeColor,
        onChangeColorBaseLayout: changeColorBaseLayout,
        onSetHoleOperation,
        getLayerSelectedObjects,
        canvas,
    } = useTools() || {}

    const palette = PALETTE;

    const [color, setColor] = useState(entryFor(value) || palette[0]);
    const [isHoleSelected, setIsHoleSelected] = useState(false);

    useEffect(() => {
        if (!canvas || !allowVaciado) return undefined;
        const refresh = () => {
            const selectedObjects = getLayerSelectedObjects?.() ?? canvas.getActiveObjects();
            const leaves = [];
            const collect = (object) => object?._objects?.length
                ? object._objects.forEach(collect)
                : leaves.push(object);
            selectedObjects.forEach(collect);
            setIsHoleSelected(leaves.length > 0 && leaves.every(object => object?._geometryRole === 'hole'));
        };
        refresh();
        const events = ['selection:created', 'selection:updated', 'selection:cleared', 'layer:changed', 'layer:nested-selection'];
        events.forEach(event => canvas.on(event, refresh));
        return () => events.forEach(event => canvas.off(event, refresh));
    }, [allowVaciado, canvas, getLayerSelectedObjects]);

    useEffect(() => {
        const selected = entryFor(value);
        if (selected) setColor(selected);
    }, [value]);

    const handleColorClick = (colorObj) => {
        setColor(colorObj);
        if (onChange) {
            onChange(colorObj.value);
            return;
        }
        if (typee === 'base_layout') {
            changeColorBaseLayout(colorObj);
        } else if (typee === 'object') {
            changeColor(colorObj);
        }
    };

    const handleVaciadoClick = () => {
        const vaciadoObj = { value: VACIADO_COLOR, description: 'Vaciado' };
        handleColorClick(vaciadoObj);
    };

    const handleHoleClick = () => {
        onSetHoleOperation?.();
        setColor({ value: '__hole__', description: 'Agujero pasante' });
        setIsHoleSelected(true);
    };

    const isVaciadoSelected = color.value === VACIADO_COLOR;

    return (
            <div className={className}>
                {/* Opción de Vaciado — solo disponible para objetos de dibujo, no para el fondo */}
                {allowVaciado && (
                    <div className="mb-3">
                        <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            onClick={handleVaciadoClick}
                            title="Vaciado / Bajo relieve: la forma se talla en el fondo en lugar de extruirse"
                            className={`relative flex min-h-16 flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg border text-xs font-medium transition-colors
                                ${isVaciadoSelected
                                    ? 'bg-base-300 border-primary text-primary ring-2 ring-primary'
                                    : 'bg-base-200 border-base-300 text-base-content hover:bg-base-300'
                                }`}
                        >
                            <Eraser className="h-5 w-5" />
                            <span>Vaciado</span>
                            {isVaciadoSelected && <Check className="absolute right-1 top-1 h-4 w-4" />}
                        </button>
                        <button
                            type="button"
                            onClick={handleHoleClick}
                            title="Agujero pasante: la forma atraviesa todo el modelo"
                            className={`flex min-h-16 flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg border text-xs font-medium transition-colors
                                ${isHoleSelected
                                    ? 'bg-error/10 border-error text-error ring-2 ring-error/60'
                                    : 'bg-base-200 border-base-300 text-base-content hover:bg-base-300'
                                }`}
                        >
                            <CircleOff className="h-5 w-5" />
                            <span>Agujero pasante</span>
                        </button>
                        </div>
                        <div className="divider my-2"></div>
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {palette.map((colorObj, index) => (
                        <div
                            key={index}
                            onClick={() => handleColorClick(colorObj)}
                            style={{
                                backgroundColor: colorObj.value,
                                width: '30px',
                                height: '30px',
                                margin: '5px',
                                cursor: 'pointer',
                                border: color.value === colorObj.value ? '2px solid black' : 'none',
                            }}
                            title={colorObj.description}
                        ></div>
                    ))}
                </div>
                <CustomColorControls
                    activeColor={color.value}
                    canvasPick={typee === 'object' || typee === 'base_layout'}
                    onPick={(hex) => handleColorClick({ value: hex, description: 'Personalizado' })}
                />
                <p className="text-neutral mt-2">
                    {isHoleSelected
                        ? 'Modo: Agujero pasante'
                        : isVaciadoSelected
                        ? 'Modo: Vaciado / Bajo relieve'
                        : `Color seleccionado: ${color.value}`}
                </p>
            </div>
    );
};

export default ColorPicker;

ColorPicker.propTypes = {
    typee: PropTypes.oneOf(['base_layout', 'object']),
    className: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    allowVaciado: PropTypes.bool,
};
